import type { AgendaItem as AgendaItemType } from '@/types';

interface Props {
  item: AgendaItemType;
  sessionStartTime: number;
  skipped?: boolean;
}

function getUrgencyLevel(sessionStartTime: number): number {
  const minutes = (Date.now() - sessionStartTime) / 60000;
  if (minutes < 2) return 0;
  if (minutes < 4) return 1;
  return 2;
}

export function AgendaItem({ item, sessionStartTime, skipped = false }: Props) {
  const { covered, text, coveredAt, level, isGroupHeader } = item;
  const urgency = covered ? -1 : getUrgencyLevel(sessionStartTime);

  const formatTime = (ts: number): string => {
    const elapsed = ts - sessionStartTime;
    const mins = Math.floor(elapsed / 60000);
    const secs = Math.floor((elapsed % 60000) / 1000);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Indentation based on level (0 = none, 1 = 1rem, 2 = 2rem)
  const indent = isGroupHeader ? 0 : level * 16; // 16px per level

  // Group headers get special styling
  if (isGroupHeader) {
    return (
      <div className="mt-3 mb-1 first:mt-0">
        <div className="flex items-center gap-2 py-1.5 px-2 bg-gray-100 rounded-md border-l-4 border-l-blue-500">
          <span className="text-blue-600 font-semibold text-xs uppercase tracking-wide">{text}</span>
        </div>
      </div>
    );
  }

  // Covered items
  if (covered) {
    return (
      <div
        className="flex items-start gap-2 py-1.5 px-3 rounded-lg bg-green-50 border-l-4 border-l-green-500 opacity-40 transition-all duration-500"
        style={{ marginLeft: indent }}
      >
        <span className="text-green-600 text-sm">✓</span>
        <span className="text-gray-500 line-through text-sm flex-grow">{text}</span>
        {coveredAt && <span className="text-xs text-green-600">{formatTime(coveredAt)}</span>}
      </div>
    );
  }

  // Skipped items get purple styling (overrides urgency)
  if (skipped) {
    return (
      <div
        className="flex items-start gap-2 py-1.5 px-3 rounded-lg border-l-4 border-l-purple-500 bg-purple-50 transition-all duration-300 animate-pulse"
        style={{ marginLeft: indent }}
      >
        <span className="text-purple-500 text-sm">⚠</span>
        <span className="text-purple-700 text-sm font-medium">{text}</span>
        <span className="text-xs text-purple-500 ml-auto">skipped</span>
      </div>
    );
  }

  // Normal uncovered items with urgency styling
  const bgColor = urgency === 0 ? 'bg-white' : urgency === 1 ? 'bg-yellow-50' : 'bg-orange-50';
  const borderColor = urgency === 0 ? 'border-l-gray-200' : urgency === 1 ? 'border-l-yellow-400' : 'border-l-orange-400';

  return (
    <div
      className={`flex items-start gap-2 py-1.5 px-3 rounded-lg border-l-4 ${borderColor} ${bgColor} transition-all duration-300`}
      style={{ marginLeft: indent }}
    >
      <span className="text-gray-400 text-sm">○</span>
      <span className="text-gray-700 text-sm">{text}</span>
    </div>
  );
}
